import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import RangeSlider from 'react-bootstrap-range-slider';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import { displayMoney } from '../../services/utilityFunctions';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import currencyProp from '../profile/propTypes/currencyProp';
import translate from '../../commonComponents/translate';
import JourneysTagProp from '../../commonComponents/propTypes/JourneysTagProp';
import PriceRangeProp from '../academy/propTypes/PriceRangeProp';

const FILTERS = {
  LANGUAGE: 'language',
  TAGS: 'tags',
};

function JourneyFilters(props) {
  const {
    t,
    languages,
    currencies,
    selected,
    tags,
    onChange,
    onPriceChange,
    priceRange,
  } = props;

  const { loggedUser } = useContext(LoggedUserContext);

  const [openItems, setOpenItems] = useState([
    FILTERS.LANGUAGE,
  ]);
  const toggleItem = filter => () =>
    setOpenItems(
      open =>
        open.includes(filter)
          ? open.filter(f => f !== filter)
          : open.concat(filter),
    );

  const handleChange = value => evt => {
    evt.preventDefault();
    onChange(value);
  };

  const handleCertificateToggle = () => {
    onChange({ certificate: selected.certificate ? undefined : true });
  };

  const allLanguages = [{ code: '', native: t('all') }].concat(languages);
  const allTags = [{ _id: '', name: t('all') }].concat(tags.map(tag => ({ _id: tag, name: tag })));

  const userCurrencyCode = priceRange.currency;
  const userCurrency = currencies.find(c => c.code === userCurrencyCode) || {};

  return (
    <div className="m-portlet m-portlet--rounded m-portlet--rounded-force">
      <div className="m-portlet__body m--padding-top-5 m--padding-bottom-20 shop-sidebar">
        <div className="row">
          <div className="col-lg-12">
            <br />
            <div className="shop-sidebar-categories">
              <ul className="list-group">
                <h5>{t('academy_filters')}</h5>
                {allTags.length > 1 && (
                  <li
                    key={FILTERS.TAGS}
                    className={cx(
                      'list-group-item',
                      'd-flex',
                      'justify-content-between',
                      'align-items-center',
                      { selected: openItems.includes(FILTERS.TAGS) },
                    )}
                    onClick={toggleItem(FILTERS.TAGS)}
                  >
                    {t('academy_tags')}
                  </li>
                )}
                {allTags.length > 1 && (
                  <ul
                    key={`${FILTERS.TAGS}-list`}
                    className="sub-list"
                    type="disc"
                  >
                    {allTags.map(tag => (
                      <li key={tag._id}>
                        <a
                          href=""
                          className={cx({
                            selected: tag._id === selected.tags,
                          })}
                          onClick={handleChange({ tags: tag._id })}
                        >
                          {tag.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                <li
                  key={FILTERS.LANGUAGE}
                  className={cx(
                    'list-group-item',
                    'd-flex',
                    'justify-content-between',
                    'align-items-center',
                    { selected: openItems.includes(FILTERS.LANGUAGE) },
                  )}
                  onClick={toggleItem(FILTERS.LANGUAGE)}
                >
                  {t('academy_language')}
                </li>
                <ul
                  key={`${FILTERS.LANGUAGE}-list`}
                  className="sub-list"
                  type="disc"
                >
                  {allLanguages.map(language => (
                    <li key={language.code}>
                      <a
                        href=""
                        className={cx({
                          selected: language.code === selected.language,
                        })}
                        onClick={handleChange({ language: language.code })}
                      >
                        {language.native}
                      </a>
                    </li>
                  ))}
                </ul>
              </ul>
            </div>
            <div className="shop-sidebar-price">
              <h5>{t('academy_max_price')}</h5>
              <RangeSlider
                variant="primary"
                tooltip="off"
                max={priceRange.max}
                min={priceRange.min}
                value={selected.maxPrice}
                onChange={({ target }) => onPriceChange('maxPrice')(parseInt(target.value, 10))}
              />
              <div className="price-range-values">
                <span className="initial-value">{t('free')}</span>
                <span className="current-value">
                  {selected.maxPrice
                    ? displayMoney(selected.maxPrice, userCurrency.symbol)
                    : t('free')}
                </span>
                <span className="final-value">
                  {displayMoney(priceRange.max, userCurrency.symbol)}
                </span>
              </div>
              <br />
            </div>
            <div className="shop-sidebar-size">
              <h5>{t('academy_certificate')}</h5>
              <form className="form">
                <div className="form-group row">
                  <div className="col-9 col-form-label">
                    <div className="checkbox-list">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={selected.certificate === true}
                          onChange={handleCertificateToggle}
                        />
                        {t('academy_required')}
                        <span />
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

JourneyFilters.propTypes = {
  t: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(currencyProp.propType))
    .isRequired,
  tags: PropTypes.arrayOf(PropTypes.shape(JourneysTagProp.propType)),
  selected: PropTypes.shape({
    language: PropTypes.string.isRequired,
    maxPrice: PropTypes.number.isRequired,
    certificate: PropTypes.bool,
    tags: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onPriceChange: PropTypes.func.isRequired,
  priceRange: PropTypes.shape(PriceRangeProp.propType).isRequired,
};

JourneyFilters.defaultProps = {
  tags: [],
};

export default translate(JourneyFilters);
